import partition from 'lodash/partition';

import {renameEvent, renameProperty} from 'common/report/util';
import {getContourSegment} from '../common';
import {combine, formatPercent, keyword, percentDiff, pluralize} from './common';

function sortSegmentList(contourDetails) {
  contourDetails = [...contourDetails];
  return contourDetails.sort((cd1, cd2) => cd2.score - cd1.score);
}

function formatSegmentList({contourDetails, property}) {
  contourDetails = sortSegmentList(contourDetails);
  const segments = contourDetails.map((contourDetail) => getContourSegment({contourDetail, property}));
  if (segments.length === 0) {
    return ``;
  } else if (segments.length === 1) {
    return segments[0];
  } else if (segments.length === 2) {
    return segments[0] + ` or ` + segments[1];
  } else if (segments.length >= 3) {
    return segments[0] + `, ` + segments[1] + `, or ` + segments[2];
  }
}

function formatExpectedDiff(baseConversionRate, contourDetails) {
  contourDetails = sortSegmentList(contourDetails);
  const percents = contourDetails.map((contourDetail) => percentDiff(contourDetail.conversionRate, baseConversionRate));
  if (percents.length === 0) {
    return ``;
  } else if (percents.length === 1) {
    return formatPercent(percents[0], 0);
  } else if (percents.length === 2) {
    const sortedPercents = percents.slice(0, 2).sort((a, b) => a - b);
    return formatPercent(sortedPercents[0], 0).slice(0, -1) + `-` + formatPercent(sortedPercents[1], 0);
  } else if (percents.length >= 3) {
    const sortedPercents = percents.slice(0, 3).sort((a, b) => a - b);
    return formatPercent(sortedPercents[0], 0).slice(0, -1) + `-` + formatPercent(sortedPercents[2], 0);
  }
}

function partitionSegments(contour) {
  return partition(
    contour.contourDetails,
    (contourDetail) => contourDetail.conversionRate < contour.baseConversionRate,
  );
}

function formatPartitionedSegments(property, lowerContourDetails, higherContourDetails) {
  const segments = {};
  if (lowerContourDetails.length) {
    segments.lower = formatSegmentList({contourDetails: lowerContourDetails, property});
  }
  if (higherContourDetails.length) {
    segments.higher = formatSegmentList({contourDetails: higherContourDetails, property});
  }

  return segments;
}

function inReportInsightsMessage(contour) {
  // [x%] of [event name] happen where [property name] = [property value]. This is [higher/lower] than your
  // overall activity of [z%] of all events performed where [property name] = [property value].
  const contourDetail = contour.contourDetail;

  const segmentConversionRate = formatPercent(contourDetail.conversionRate, 2);
  const eventName = keyword(renameEvent(contour.insightsDetails.eventDisplayName));
  const propertyName = keyword(renameProperty(contour.property));
  const segment = keyword(getContourSegment({contourDetail, property: contour.property}));
  const expectedDirection = contourDetail.conversionRate < contour.baseConversionRate ? `lower` : `higher`;
  const overallConversionRate = formatPercent(contour.baseConversionRate, 2);

  return combine([
    segmentConversionRate,
    ` of `,
    eventName,
    ` events happen where `,
    propertyName,
    ` = `,
    segment,
    `. `,
    `This is `,
    expectedDirection,
    ` than your overall activity of `,
    overallConversionRate,
    ` of all events performed where `,
    propertyName,
    ` = `,
    segment,
    `.`,
  ]);
}

function globalInsightsMessage(contour, typed) {
  // For your event, [Event name], activity is lower where [property name] = [Segment1- lower, Segment2-lower,
  // or Segment3-lower] and is higher where [property name]=[segment1-higher,segment2-higher, or segment3-higher].
  const segmentDetails = [];
  const propertyName = keyword(renameProperty(contour.property));
  const [lowerContourDetails, higherContourDetails] = partitionSegments(contour);
  const {lower, higher} = formatPartitionedSegments(contour.property, lowerContourDetails, higherContourDetails);

  if (lower) {
    segmentDetails.push(`is lower where `, propertyName, ` = `, keyword(lower));
  }

  if (higher) {
    if (segmentDetails.length > 0) {
      segmentDetails.push(` and `);
    }

    segmentDetails.push(`is higher where `, propertyName, ` = `, keyword(higher));
  }

  const eventName = keyword(renameEvent(contour.insightsDetails.eventDisplayName));

  const messageParts = [`For your event, `, eventName, `, activity `, ...segmentDetails, `.`];

  if (typed) {
    messageParts.unshift(keyword(`Insights Report: `));
  }

  return combine(messageParts);
}

export function generateInsightsMessage(contour, {displayContext, typed}) {
  return displayContext === `IN_REPORT` ? inReportInsightsMessage(contour) : globalInsightsMessage(contour, typed);
}

function inReportFunnelsMessage(contour) {
  // Users where [property name] = [property value] have a [x%] conversion rate in your funnel.
  // This is [higher/lower] than your overall conversion rate of [z%].

  const {contourDetail} = contour;

  const propertyName = keyword(renameProperty(contour.property));
  const segment = keyword(getContourSegment({contourDetail, property: contour.property}));
  const segmentConversionRate = formatPercent(contourDetail.conversionRate, 2);
  const expectedDirection = contourDetail.conversionRate < contour.baseConversionRate ? `lower` : `higher`;
  const overallConversionRate = formatPercent(contour.baseConversionRate, 2);
  const expectedDiff = keyword(formatPercent(percentDiff(contourDetail.conversionRate, contour.baseConversionRate), 0));

  return combine([
    `Users where `,
    propertyName,
    ` = `,
    segment,
    ` have a `,
    segmentConversionRate,
    ` conversion rate in your funnel. This is `,
    expectedDiff,
    ` `,
    expectedDirection,
    ` than your overall conversion rate of `,
    overallConversionRate,
    `.`,
  ]);
}

function globalFunnelsMessage(contour, typed) {
  // For your funnel, [Funnel name], users where [property name] = [Segment1-lower, Segment2-lower, or
  // Segment3-lower] convert at a lower rate and users where [property name] = [Segment1-higher, Segment2-higher,
  // or Segment3-higher] convert at a higher rate.

  const [lowerContourDetails, higherContourDetails] = partitionSegments(contour);
  const {lower, higher} = formatPartitionedSegments(contour.property, lowerContourDetails, higherContourDetails);
  const propertyName = keyword(renameProperty(contour.property));
  const segmentDetails = [];

  if (lower) {
    const lowerExpectedDiff = keyword(formatExpectedDiff(contour.baseConversionRate, lowerContourDetails));
    segmentDetails.push(`users where `, propertyName, ` = `, keyword(lower), ` convert `, lowerExpectedDiff, ` worse`);
  }

  if (higher) {
    if (segmentDetails.length) {
      segmentDetails.push(` and `);
    }
    const higherExpectedDiff = keyword(formatExpectedDiff(contour.baseConversionRate, higherContourDetails));
    segmentDetails.push(
      `users where `,
      propertyName,
      ` = `,
      keyword(higher),
      ` convert `,
      higherExpectedDiff,
      ` better`,
    );
  }

  const funnelName = keyword(contour.funnelsDetails.funnelName);
  const messageParts = [`For funnel, `, funnelName, `, `, ...segmentDetails, ` than your overall user base.`];

  if (typed) {
    messageParts.unshift(keyword(`Funnels Report: `));
  }

  return combine(messageParts);
}

export function generateFunnelsMessage(contour, {displayContext, typed}) {
  return displayContext === `IN_REPORT` ? inReportFunnelsMessage(contour) : globalFunnelsMessage(contour, typed);
}

function inReportRetentionMessage(contour) {
  // Users where [property name] = [property value] have a [x%] retention rate in your funnel for
  // [p] [day/week/month] retention. This is [higher/lower] than your overall retention rate of [z%].

  const {contourDetail, retentionDetails} = contour;

  const propertyName = keyword(renameProperty(contour.property));
  const segment = keyword(getContourSegment({contourDetail, property: contour.property}));
  const segmentRetentionRate = formatPercent(contourDetail.conversionRate, 2);
  const interval = retentionDetails.interval;
  const timeUnit = pluralize(retentionDetails.timeUnit.toLowerCase(), retentionDetails.interval);
  const expectedDirection = contourDetail.conversionRate < contour.baseConversionRate ? `lower` : `higher`;
  const overallRetentionRate = formatPercent(contour.baseConversionRate, 2);
  const expectedDiff = keyword(formatPercent(percentDiff(contourDetail.conversionRate, contour.baseConversionRate), 0));
  return combine([
    `Users where `,
    propertyName,
    ` = `,
    segment,
    ` have a `,
    segmentRetentionRate,
    ` retention rate for `,
    interval,
    ` `,
    timeUnit,
    ` retention. This is `,
    expectedDiff,
    ` `,
    expectedDirection,
    ` than your overall retention rate of `,
    overallRetentionRate,
    `.`,
  ]);
}

function globalRetentionMessage(contour, typed) {
  // For retention from [Event1] to [Event2], users where [property name] = [Segment1-lower, Segment2-lower,
  // or Segment3-lower] have a lower retention rate and users where [property name] = [Segment1-higher,
  // Segment2-higher, or Segment3-higher] have a higher retention rate.

  const [lowerContourDetails, higherContourDetails] = partitionSegments(contour);
  const {lower, higher} = formatPartitionedSegments(contour.property, lowerContourDetails, higherContourDetails);
  const propertyName = keyword(renameProperty(contour.property));
  const segmentDetails = [];

  if (lower) {
    const lowerExpectedDiff = keyword(formatExpectedDiff(contour.baseConversionRate, lowerContourDetails));
    segmentDetails.push(`users where `, propertyName, ` = `, keyword(lower), ` retain `, lowerExpectedDiff, ` worse`);
  }

  if (higher) {
    if (segmentDetails.length) {
      segmentDetails.push(` and `);
    }
    const higherExpectedDiff = keyword(formatExpectedDiff(contour.baseConversionRate, higherContourDetails));
    segmentDetails.push(
      `users where `,
      propertyName,
      ` = `,
      keyword(higher),
      ` retain `,
      higherExpectedDiff,
      ` better`,
    );
  }

  const {retentionDetails} = contour;
  const bornEventName = keyword(renameEvent(retentionDetails.bornEventDisplayName));
  const eventName = keyword(renameEvent(retentionDetails.eventDisplayName));

  const messageParts = [
    `For `,
    bornEventName,
    ` to `,
    eventName,
    ` retention, `,
    ...segmentDetails,
    ` than your overall user base.`,
  ];

  if (typed) {
    messageParts.unshift(keyword(`Retention Report: `));
  }

  return combine(messageParts);
}

export function generateRetentionMessage(contour, {displayContext, typed}) {
  return displayContext === `IN_REPORT` ? inReportRetentionMessage(contour) : globalRetentionMessage(contour, typed);
}
